import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

import { toast } from 'react-toastify';
import styled from 'styled-components';

import { PAGES } from 'constants/pages';

import { Header, HeaderTitle } from 'components/Header';
import { PageTitle } from 'components/PageTitle';
import { ActionsAndSave, type PageActionGroup } from 'components/PageHeader';

import { EquipmentFormContent } from './components/EquipmentFormContent';

import { useCreateEquipmentMutation } from 'data/__generated__';

type FormData = {
    name: string;
    brand: string;
    volume: string;
    price: number;
    description: string;
    hidden: boolean;
    height: number;
    width: number;
    length: number;
    weight: number;
};

export const EquipmentCreate = () => {
    const navigate = useNavigate();

    const [createEquipment, { loading: createEquipmentLoading }] = useCreateEquipmentMutation();

    const methods = useForm<FormData>();
    const {
        handleSubmit,
        formState: { isDirty },
    } = methods;

    const onSubmit = handleSubmit(
        async (fields) => {
            const { data } = await createEquipment({
                variables: {
                    createFields: fields,
                },
            });
            if (data) {
                const {
                    createEquipment: { equipment, success, error },
                } = data;
                if (success && equipment) {
                    toast.success(`L'équipement "${equipment.name}" a bien été créé !`);
                    navigate(`/equipment/${equipment._id}`);
                } else {
                    if (error) {
                        toast.error(error, { autoClose: false });
                    } else {
                        throw Error("Une erreur inconnue s'est produite");
                    }
                }
            } else {
                throw Error("Une erreur inconnue s'est produite");
            }
        },
        () => {
            toast.error("Le formulaire n'est pas valide. Veuillez corriger les erreurs avant de continuer.");
        },
    );

    const actionGroups: PageActionGroup[] = [
        {
            label: 'Navigation',
            items: [{ label: 'Retour à la liste', icon: <ArrowLeft />, to: '/equipments' }],
        },
    ];

    return (
        <Container>
            <FormProvider {...methods}>
                <Form onSubmit={onSubmit}>
                    <Header>
                        <HeaderTitle>
                            <PageTitle page={PAGES.equipmentCreate} />
                        </HeaderTitle>
                        <ActionsAndSave
                            actionGroups={actionGroups}
                            onSave={onSubmit}
                            saveDisabled={!isDirty}
                            saveLoading={createEquipmentLoading}
                            saveTitle="Créer"
                            saveDataTest="create-equipment-button"
                        />
                    </Header>
                    <Content>
                        <ScrollableContent>
                            <EquipmentFormContent />
                        </ScrollableContent>
                    </Content>
                </Form>
            </FormProvider>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
`;

const Content = styled.div`
    flex: 1;
    overflow: hidden;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const ScrollableContent = styled.div`
    padding: 15px;
    width: 100%;
    height: 100%;
    overflow-y: auto;
`;
